import React, { useCallback, useEffect, useMemo, useState } from "react";
import { css } from "@emotion/react";
import { Theme } from "../../styles/theme";
import styled from "@emotion/styled";
import { Amount } from "../../types/amount";
import { FilterDropdownButton } from "./FilterDropdownButton";
import { Filter, FiltersInterface } from "../../config/consts";
import { isZero } from "../../utils/amountUtils";
import { CurrencyInput } from "../general/CurrencyInput/CurrencyInput";
import { Table } from "@tanstack/react-table";
import { TransactionDetail } from "types/history";
import { GlobalStore } from "../../store/global/GlobalStore";

interface AmountFilterProps {
  table: Table<TransactionDetail>;
  confirmedOptions: FiltersInterface[Filter.Amount];
  setConfirmedOptions: (updatedOptions: FiltersInterface[Filter.Amount]) => void;
}

export const AmountFilter: React.FC<AmountFilterProps> = ({
  table,
  confirmedOptions,
  setConfirmedOptions,
}) => {
  const [minInputRef, setMinInputRef] = useState<HTMLInputElement | null>(null);
  const [maxInputRef, setMaxInputRef] = useState<HTMLInputElement | null>(null);

  const theme = GlobalStore.useState(s => s.theme);
  const [minMaxValues, setMinMaxValues] = useState<FiltersInterface[Filter.Amount]>(undefined);
  const [isOnDoneClickedWithMinAboveMax, setIsOnDoneClickedWithMinAboveMax] = useState<
    boolean | undefined
  >(undefined);
  const isMinAboveMax =
    minMaxValues?.min !== undefined &&
    minMaxValues.max !== undefined &&
    minMaxValues.min.value.greaterThan(minMaxValues.max.value);
  const showInputHasError = useMemo(() => {
    if (!isOnDoneClickedWithMinAboveMax || (!isMinAboveMax && isOnDoneClickedWithMinAboveMax)) {
      setIsOnDoneClickedWithMinAboveMax(false);
      return false;
    }
    return true;
  }, [isMinAboveMax, isOnDoneClickedWithMinAboveMax]);

  const onClear = useCallback(() => {
    setMinMaxValues(undefined);
  }, []);

  const additionalOnDone = useCallback(() => {
    if (isMinAboveMax) {
      setIsOnDoneClickedWithMinAboveMax(true);
      return;
    }
    setIsOnDoneClickedWithMinAboveMax(false);
  }, [isMinAboveMax]);

  useEffect(() => {
    if (confirmedOptions == null) {
      setMinMaxValues(undefined);
      return;
    }
    setMinMaxValues({ min: confirmedOptions.min, max: confirmedOptions.max });
  }, [confirmedOptions]);

  const updateMinAndMax = useCallback(
    (amountValue: Amount, type: "min" | "max", wasNumberPressed: boolean) => {
      setMinMaxValues(prevValues => {
        const isValueZero = isZero(amountValue.value.toString());
        const newValue = isValueZero ? undefined : amountValue;

        if (!wasNumberPressed) {
          return undefined;
        }

        if (!prevValues) {
          return {
            min: type === "min" ? newValue : undefined,
            max: type === "max" ? newValue : undefined,
          };
        }

        if (
          newValue === undefined &&
          (prevValues.min === undefined || prevValues.max === undefined)
        ) {
          return undefined;
        }

        return {
          ...prevValues,
          [type]: newValue,
        };
      });
    },
    []
  );

  return (
    <FilterDropdownButton
      filterType={Filter.Amount}
      localOptions={minMaxValues}
      setLocalOptions={() => onClear()}
      confirmedOptions={confirmedOptions}
      setConfirmedOptions={setConfirmedOptions}
      table={table}
      additionalOnDone={additionalOnDone}
      preventFiltering={isMinAboveMax}
      onShown={() => {
        if (minMaxValues?.min === undefined) {
          minInputRef?.focus();
        } else if (minMaxValues?.max === undefined) {
          maxInputRef?.focus();
        }
      }}
    >
      <AmountFilterContainer>
        <div>
          <AmountFilterHeading>Min USD amount</AmountFilterHeading>
          <CurrencyInput
            ref={setMinInputRef}
            style={inputCss(theme, showInputHasError)}
            placeholder="Enter min amount..."
            prefix="$"
            value={confirmedOptions?.min}
            onValueChange={useCallback(
              (value: Amount, wasNumberPressed: boolean) => {
                updateMinAndMax(value, "min", wasNumberPressed);
              },
              [updateMinAndMax]
            )}
            id="min"
            clearButton={true}
          />
        </div>
        <div
          css={css`
            margin-top: 14px;
          `}
        >
          <AmountFilterHeading>Max USD amount</AmountFilterHeading>
          <CurrencyInput
            ref={setMaxInputRef}
            style={inputCss(theme, showInputHasError)}
            placeholder="Enter max amount..."
            prefix="$"
            value={confirmedOptions?.max}
            onValueChange={useCallback(
              (value: Amount, wasNumberPressed: boolean) => {
                updateMinAndMax(value, "max", wasNumberPressed);
              },
              [updateMinAndMax]
            )}
            id="max"
            clearButton={true}
          />
        </div>
      </AmountFilterContainer>
    </FilterDropdownButton>
  );
};

// Styles

const AmountFilterContainer = styled.div`
  min-height: 42px;
  padding: 8px;
`;

const AmountFilterHeading = styled.h6`
  color: ${props => props.theme.font.secondary.default};
  font: ${props => props.theme.Desktop.Paragraph.Large};
`;

const inputCss = (theme: Theme, isError: boolean) => css`
  background: ${theme.surface.alt.primary.default};
  border-radius: 14px;
  height: 42px;
  border: none;
  width: 100%;
  padding: 10px;
  color: ${theme.font.primary.default};
  font: ${theme.Desktop.Paragraph.Large};
  border: ${isError ? `1px solid ${theme["Brand colours"].Red}` : "none"};
  font-size: 14px;
  text-align: left;
  margin-top: 4px;
`;
